import type { TableSortOption } from './TableSortPopover';
import type {
  TableColumnOption,
  TableQuickSortOption,
  TableStatusOption,
} from './tableToolbar.types';
import type { GridColumnVisibilityModel } from '@/ui/primitives';

export type TableColumnDefinition = {
  field: string;
  label: string;
  mandatory?: boolean;
  defaultVisible?: boolean;
  userVisible?: boolean;
};

export type TableDefinition<TSortKey extends string, TStatus extends string> = {
  title: string;
  searchAriaLabel: string;
  columns: TableColumnDefinition[];
  defaultColumnVisibilityModel: GridColumnVisibilityModel;
  columnOptions: TableColumnOption[];
  defaultSortKey: TSortKey;
  defaultSortDirection: 'asc' | 'desc';
  sortOptions: Array<TableSortOption<TSortKey>>;
  quickSortOptions?: Array<TableQuickSortOption<TSortKey>>;
  sortFieldMap: Record<TSortKey, string>;
  statusOptions: Array<TableStatusOption<TStatus>>;
  includeLatestQuickFilter?: boolean;
  statusFilterLabel?: string;
};

type CreateTableDefinitionInput<TSortKey extends string, TStatus extends string> = Omit<
  TableDefinition<TSortKey, TStatus>,
  'defaultColumnVisibilityModel' | 'columnOptions'
>;

const buildDefaultColumnVisibilityModel = (
  columns: TableColumnDefinition[],
): GridColumnVisibilityModel =>
  columns.reduce<GridColumnVisibilityModel>((acc, column) => {
    acc[column.field] = column.mandatory ? true : (column.defaultVisible ?? false);
    return acc;
  }, {});

const buildColumnOptions = (columns: TableColumnDefinition[]): TableColumnOption[] =>
  columns
    .filter((column) => column.userVisible !== false)
    .map(({ field, label, mandatory }) => ({ field, label, mandatory }));

export const createTableDefinition = <TSortKey extends string, TStatus extends string>(
  input: CreateTableDefinitionInput<TSortKey, TStatus>,
): TableDefinition<TSortKey, TStatus> => ({
  ...input,
  defaultColumnVisibilityModel: buildDefaultColumnVisibilityModel(input.columns),
  columnOptions: buildColumnOptions(input.columns),
});
